// ============================================================
// PLUMME 3 — Privat økonomi · perioder
// Forbrukssiden kan vise en enkelt måned eller hele året. Denne fila eier
// periodelogikken: hvilke måneder som har importert forbruk, hvilken periode
// som er standard, og hva hver kategori står for i valgt periode.
// Kategorilista fra readmodellen er fasit for hva som regnes som forbruk, slik
// at avdrag og overføringer ikke lekker inn i tallene.
// ============================================================
(function personalFinancePeriods(global) {
  const PO_PERIOD_YEAR = 'year';
  const PO_PERIOD_MONTH_TO_DATE = 'month-to-date';
  const PO_PERIOD_ROLLING_30 = 'rolling-30';
  const PO_PERIOD_IMPORT = 'import';

  const poMonthKey = (year, month) => `${year}-${String(month).padStart(2, '0')}`;
  const poPreviousMonthKey = (year, month) => (month === 1 ? poMonthKey(year - 1, 12) : poMonthKey(year, month - 1));

  // Ett gjennomløp over transaksjonene gir beløp per kategori per måned.
  function poMonthlyByCategory(model) {
    const known = new Set((model?.categories || []).map((entry) => entry.category));
    const byCategory = new Map();
    (model?.transactions || []).forEach((transaction) => {
      const amount = Number(transaction?.amount || 0);
      const category = transaction?.category;
      if (amount >= 0 || !category || !known.has(category)) return;
      const key = String(transaction.date || transaction.month || '').slice(0, 7);
      if (key.length !== 7) return;
      const months = byCategory.get(category) || new Map();
      months.set(key, (months.get(key) || 0) + Math.abs(amount));
      byCategory.set(category, months);
    });
    return byCategory;
  }

  // Månedsnumre (1–12) med registrert forbruk i året, stigende.
  function poExpenseMonths(model, year) {
    const prefix = `${year}-`;
    const months = new Set();
    poMonthlyByCategory(model).forEach((byMonth) => {
      byMonth.forEach((amount, key) => {
        if (!amount || !key.startsWith(prefix)) return;
        const month = Number(key.slice(5, 7));
        if (month >= 1 && month <= 12) months.add(month);
      });
    });
    return [...months].sort((a, b) => a - b);
  }

  // Siste måned med data er standardperioden. Uten data faller vi tilbake til
  // året, slik at tomtilstanden ser ut som før periodevelgeren kom.
  function poDefaultPeriod(months) {
    return months.length ? months[months.length - 1] : PO_PERIOD_YEAR;
  }

  const poIsYearPeriod = (period) => !period || period === PO_PERIOD_YEAR;

  const poDate = (value) => {
    const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(value || ''));
    if (!match) return null;
    const date = new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3])));
    return Number.isNaN(date.getTime()) ? null : date;
  };
  const poIso = (date) => date.toISOString().slice(0, 10);
  const poAddDays = (date, days) => new Date(date.getTime() + days * 86400000);
  const poEndOfMonth = (year, month) => new Date(Date.UTC(year, month, 0));

  function poPeriodDescriptor(period, year, transactions = [], today = new Date()) {
    const txDates = transactions.map((entry) => poDate(entry.date)).filter(Boolean).sort((a, b) => a - b);
    let start;
    let end;
    let previousStart;
    let previousEnd;
    let kind = period;
    if (poIsYearPeriod(period)) {
      kind = PO_PERIOD_YEAR;
      start = new Date(Date.UTC(year, 0, 1));
      end = new Date(Date.UTC(year, 11, 31));
      previousStart = new Date(Date.UTC(year - 1, 0, 1));
      previousEnd = new Date(Date.UTC(year - 1, 11, 31));
    } else if (period === PO_PERIOD_ROLLING_30) {
      end = new Date(Date.UTC(today.getFullYear(), today.getMonth(), today.getDate()));
      start = poAddDays(end, -29);
      previousEnd = poAddDays(start, -1);
      previousStart = poAddDays(previousEnd, -29);
    } else if (period === PO_PERIOD_MONTH_TO_DATE) {
      end = new Date(Date.UTC(today.getFullYear(), today.getMonth(), today.getDate()));
      start = new Date(Date.UTC(today.getFullYear(), today.getMonth(), 1));
      previousStart = new Date(Date.UTC(today.getFullYear(), today.getMonth() - 1, 1));
      previousEnd = new Date(Date.UTC(today.getFullYear(), today.getMonth() - 1, today.getDate()));
      const previousMonthEnd = poEndOfMonth(previousStart.getUTCFullYear(), previousStart.getUTCMonth() + 1);
      if (previousEnd > previousMonthEnd) previousEnd = previousMonthEnd;
    } else if (period === PO_PERIOD_IMPORT) {
      start = txDates[0] || new Date(Date.UTC(year, 0, 1));
      end = txDates.at(-1) || new Date(Date.UTC(year, 11, 31));
    } else {
      const month = Math.max(1, Math.min(12, Number(period) || 1));
      kind = 'month';
      start = new Date(Date.UTC(year, month - 1, 1));
      end = poEndOfMonth(year, month);
      previousStart = new Date(Date.UTC(year, month - 2, 1));
      previousEnd = poEndOfMonth(previousStart.getUTCFullYear(), previousStart.getUTCMonth() + 1);
    }
    return {
      endDate: poIso(end),
      kind,
      previousEndDate: previousEnd ? poIso(previousEnd) : '',
      previousStartDate: previousStart ? poIso(previousStart) : '',
      startDate: poIso(start),
    };
  }

  const poInRange = (transaction, startDate, endDate) => {
    const value = String(transaction?.date || transaction?.month || '').slice(0, 10);
    return value >= startDate && value <= endDate;
  };
  const poTransactionsForDescriptor = (transactions, descriptor) =>
    (transactions || []).filter((entry) => poInRange(entry, descriptor.startDate, descriptor.endDate));

  function poSummaryForTransactions(transactions = []) {
    const summary = { creditCardPayments: 0, debtPayments: 0, expenses: 0, income: 0, savings: 0, transfers: 0 };
    transactions.forEach((transaction) => {
      const amount = Math.abs(Number(transaction?.amount || 0));
      const type = transaction?.type || (Number(transaction?.amount || 0) >= 0 ? 'income' : 'expense');
      if (type === 'income') summary.income += amount;
      else if (type === 'debt_payment') summary.debtPayments += amount;
      else if (type === 'saving') summary.savings += amount;
      else if (type === 'transfer') summary.transfers += amount;
      else if (type === 'credit_card_payment') summary.creditCardPayments += amount;
      else if (type === 'expense') summary.expenses += amount;
    });
    summary.netCashflow = summary.income - summary.expenses - summary.debtPayments - summary.savings;
    summary.transactions = transactions.length;
    return summary;
  }

  function poPeriodLabel(descriptor, locale = 'nb-NO') {
    const start = poDate(descriptor?.startDate);
    const end = poDate(descriptor?.endDate);
    if (!start || !end) return '';
    const sameMonth = start.getUTCFullYear() === end.getUTCFullYear() && start.getUTCMonth() === end.getUTCMonth();
    if (descriptor.kind === PO_PERIOD_YEAR) return String(start.getUTCFullYear());
    if (sameMonth && start.getUTCDate() === 1 && end.getUTCDate() === poEndOfMonth(end.getUTCFullYear(), end.getUTCMonth() + 1).getUTCDate()) {
      return new Intl.DateTimeFormat(locale, { month: 'long', year: 'numeric', timeZone: 'UTC' }).format(start);
    }
    const startText = new Intl.DateTimeFormat(locale, { day: 'numeric', month: sameMonth ? undefined : 'short', timeZone: 'UTC' }).format(start);
    const endText = new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC' }).format(end);
    return `${startText}–${endText}`;
  }

  // Budsjettgrensene er månedlige. Årsvisningen sammenlignes derfor mot
  // grensen ganget med antall måneder som faktisk har data.
  function poPeriodMonthCount(model, year, period) {
    if (!poIsYearPeriod(period)) return 1;
    return Math.max(1, poExpenseMonths(model, year).length);
  }

  // Legger periodebeløp og endring mot forrige måned på hver forbrukskategori.
  function poCategoriesForPeriod(model, year, period, color) {
    const byCategory = poMonthlyByCategory(model);
    const isYear = poIsYearPeriod(period);
    const months = poExpenseMonths(model, year);
    const selected = isYear ? (months.length ? months[months.length - 1] : 0) : Number(period) || 0;
    const currentKey = selected ? poMonthKey(year, selected) : '';
    const previousKey = selected ? poPreviousMonthKey(year, selected) : '';
    return (model?.categories || []).map((entry, index) => {
      const byMonth = byCategory.get(entry.category) || new Map();
      const now = currentKey ? byMonth.get(currentKey) || 0 : 0;
      const before = previousKey ? byMonth.get(previousKey) || 0 : 0;
      return {
        ...entry,
        amount: isYear ? Number(entry.amount || 0) : now,
        changePct: before ? ((now - before) / Math.abs(before)) * 100 : 0,
        color: typeof color === 'function' ? color(index) : entry.color,
        monthAmount: now,
        period: isYear ? PO_PERIOD_YEAR : selected,
        prevMonthAmount: before,
        year,
      };
    });
  }

  global.PoPeriods = {
    IMPORT: PO_PERIOD_IMPORT,
    MONTH_TO_DATE: PO_PERIOD_MONTH_TO_DATE,
    ROLLING_30: PO_PERIOD_ROLLING_30,
    YEAR: PO_PERIOD_YEAR,
    categoriesForPeriod: poCategoriesForPeriod,
    defaultPeriod: poDefaultPeriod,
    expenseMonths: poExpenseMonths,
    isYearPeriod: poIsYearPeriod,
    monthCount: poPeriodMonthCount,
    describe: poPeriodDescriptor,
    format: poPeriodLabel,
    summary: poSummaryForTransactions,
    transactions: poTransactionsForDescriptor,
  };
}(window));
